// A Scene is the script behind an Academy lesson's "living video": the real
// app runs inside a same-origin iframe (stage mode, see ../stage) against a
// fixture API, and the ScenePlayer performs these actions on the live DOM
// while the lesson page plays the narration. Nothing here is a recording;
// when the UI changes, the scene re-renders with today's UI.
//
// Actions run sequentially within a step. A step ends when its actions are
// done AND its narration clip has finished (or, with no clip, after
// `minDurationMs`). The parent page owns the audio; the iframe owns the
// actions. See ../stage/protocol.ts for the postMessage contract.
import { ACADEMY_ASSETS_URL } from "../assets";

export type SceneAction =
  /** Glide the fake cursor onto the element (center). */
  | { type: "moveTo"; target: string; /** Also fire mouseover so hover cards open (closed when the cursor next moves). */ hover?: boolean }
  /** Glide to the element and click it (a real `.click()` on the element). */
  | { type: "click"; target: string; /** Send mousedown/mouseup first, as a real pointer does (ProseMirror decorations). */ pointer?: boolean }
  /** Focus the element and type text, one character per `1000/cps` ms. */
  | { type: "type"; target: string; text: string; cps?: number; replace?: boolean }
  /**
   * Fetch `url` (a file under /academy/samples) and hand it to the file input
   * matched by `target` as if the user had picked it. Dispatches a bubbling
   * `change` event so React's handler runs.
   */
  | { type: "attachFile"; target: string; url: string; name: string; mime: string }
  /** Pause for a moment (let the viewer read). */
  | { type: "wait"; ms: number }
  /** Select the element's text and type over it (a placeholder in the editor). */
  | { type: "typeOver"; target: string; text: string; cps?: number }
  /**
   * Select text inside the element (the first occurrence of `text`, or all of
   * its contents) with the editor focused, as a drag-select would. With
   * `collapse` the selection becomes a caret at that edge (click into a
   * paragraph at its end to type there).
   */
  | { type: "selectText"; target: string; text?: string; collapse?: "start" | "end" }
  /** Glide to a native `<select>` and choose the option whose value (or label) is `value`. */
  | { type: "selectOption"; target: string; value: string }
  /** Drag the element and drop it on `to` (HTML5 drag and drop, e.g. reordering upload rows). */
  | { type: "dragTo"; target: string; to: string }
  /** Poll until the element exists (and is visible); fails the scene on timeout. */
  | { type: "waitFor"; target: string; timeoutMs?: number }
  /** Scroll the element into view (smooth). */
  | { type: "scrollIntoView"; target: string }
  /** Draw (or clear, with null) a soft ring around the element. */
  | { type: "spotlight"; target: string | null }
  /** Mutate the fixture store (see ../stage/fixtures/store.ts). */
  | { type: "fixture"; name: string; args?: unknown }
  /** Client-side navigate inside the stage. */
  | { type: "navigate"; to: string };

export interface SceneStep {
  /** Stable id; also the narration clip's key in the manifest. */
  id: string;
  /** What is spoken. Also the caption. The narration CLI reads this text. */
  narration: string;
  /** Shorter on-screen caption when the narration is long. */
  caption?: string;
  actions: SceneAction[];
  /** With no narration clip, the step lasts at least this long (default 1500). */
  minDurationMs?: number;
}

export interface Scene {
  id: string;
  title: string;
  /** Route the stage iframe boots on (and reloads to when seeking back). */
  startRoute: string;
  /** Name of the fixture set to load (see ../stage/fixtures). */
  fixture: string;
  /** Fixed stage viewport; the lesson page scales it to fit. */
  viewport: { width: number; height: number };
  steps: SceneStep[];
}

export interface NarrationManifest {
  sceneId: string;
  voiceId: string;
  modelId: string;
  generatedAt: string;
  steps: Record<string, { file: string; durationMs: number; hash: string }>;
}

// 1440 wide: the sidebar (min 250) and the cases list (seeded at 232) are
// fixed-width panels, so a wider stage hands all of the extra room to the
// editor, which is what most lessons are about. 16:10 keeps it short enough
// to sit above the fold once scaled into the lesson column.
export const DEFAULT_STAGE_VIEWPORT = { width: 1440, height: 900 } as const;

/** Downloadable sample files (S3). */
export const SAMPLES_BASE = `${ACADEMY_ASSETS_URL}/samples`;
/** Narration mp3s (S3), `<sceneId>/<stepId>.mp3`. */
export const NARRATION_BASE = `${ACADEMY_ASSETS_URL}/narration`;
/** Narration manifests (repo, served by the app), `<sceneId>/manifest.json`. */
export const NARRATION_MANIFEST_BASE = "/academy/narration";
